import { gql, useMutation } from '@apollo/client';

import type {
  UpdateFinancialAccountSettingsMutation,
  UpdateFinancialAccountSettingsMutationVariables,
} from './__generated__/useUpdateFinancialAccountSettings.gql';

// The selection must mirror every `settings` field the UI reads — `AccountSettings` has no id, so
// Apollo replaces the whole sub-object on the normalized `FinancialAccount`; a field read but not
// selected here would silently go stale.
const UPDATE_FINANCIAL_ACCOUNT_SETTINGS = gql`
  mutation UpdateFinancialAccountSettings($input: UpdateFinancialAccountSettingsInput!) {
    updateFinancialAccountSettings(input: $input) {
      id
      settings {
        lowBalanceThreshold
        creditUtilizationThreshold
        disableThreshold
      }
    }
  }
`;

/**
 * Apollo mutation hook for one account's per-account notification settings — a
 * threshold override (`lowBalanceThreshold` / `creditUtilizationThreshold`, or
 * `null` to inherit the household default) and/or `disableThreshold` mute. The
 * selection returns `id` plus every `settings` field so Apollo's whole-object
 * replacement of the id-less `AccountSettings` keeps every reader current.
 *
 * @returns the standard `useMutation` tuple
 *   `[updateFinancialAccountSettings, { loading, error, ... }]`.
 */
export const useUpdateFinancialAccountSettings = () => {
  return useMutation<UpdateFinancialAccountSettingsMutation, UpdateFinancialAccountSettingsMutationVariables>(
    UPDATE_FINANCIAL_ACCOUNT_SETTINGS
  );
};
